// C.2: Admin User Directory. Rich, filter-first browse of every user
// in the system with a per-row side drawer that shows the full user
// object, progression ledger, achievements, referrals, and RSVP
// history. Read-only. Role management lives on /admin/user-roles.
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Users, User, Search, Filter, Shield, ArrowUpDown, ChevronRight,
  Instagram, Linkedin, Twitter, Facebook, Music2, Trophy, TrendingUp, Ticket,
  Share2, Mail, Phone as PhoneIcon, ExternalLink,
} from 'lucide-react';

const _initials = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  const first = parts[0][0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase().slice(0, 2);
};

const _fmtDate = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return String(iso).slice(0, 10); }
};

const useDebounced = (value, delay = 300) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

const AvatarCell = ({ url, name }) => {
  const [broken, setBroken] = useState(false);
  if (broken || !url) {
    return (
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-100 to-secondary-100 flex items-center justify-center text-[11px] font-semibold text-primary-900">
        {_initials(name)}
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={name}
      className="w-9 h-9 rounded-full object-cover"
      onError={() => setBroken(true)}
      loading="lazy"
    />
  );
};

const RolePill = ({ role }) => {
  const map = {
    super_admin: 'bg-primary-700 text-white',
    admin: 'bg-primary-100 text-primary-900',
    user: 'bg-neutral-100 text-neutral-700',
    guest: 'bg-secondary-100 text-secondary-900',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${map[role] || map.user}`}>
      {role || 'user'}
    </span>
  );
};

const LevelPill = ({ prog }) => {
  if (!prog) return <span className="text-xs text-neutral-400">-</span>;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-semibold text-primary-900">L{prog.level}</span>
      <span className="text-[10px] text-neutral-500">{prog.rank || prog.stage || 'Naya Dost'}</span>
    </div>
  );
};

const AdminUsersPage = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [cursor, setCursor] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emptyProfileCount, setEmptyProfileCount] = useState(null);
  const [unlinkedRsvpCount, setUnlinkedRsvpCount] = useState(null);
  const [incompleteNamesCount, setIncompleteNamesCount] = useState(null);
  const [incompleteNamesFilter, setIncompleteNamesFilter] = useState(false);
  const [q, setQ] = useState('');
  const debouncedQ = useDebounced(q, 350);
  const [role, setRole] = useState('');
  const [signupSince, setSignupSince] = useState('all');
  const [hasReg, setHasReg] = useState('any');
  const [sortBy, setSortBy] = useState('created_at');
  const [drawerUser, setDrawerUser] = useState(null);
  // Bundle 149b - blocked-under-18 surfacing.
  const [blockedUnder18, setBlockedUnder18] = useState([]);
  const [blockedUnder18Filter, setBlockedUnder18Filter] = useState(false);

  const fetchPage = async (opts = {}) => {
    setLoading(true);
    try {
      const params = {
        enrich: true,
        limit: 50,
        q: debouncedQ || undefined,
        role: role || undefined,
        signup_since: signupSince !== 'all' ? signupSince : undefined,
        has_active_registration:
          hasReg === 'yes' ? true : hasReg === 'no' ? false : undefined,
        incomplete_names: incompleteNamesFilter ? true : undefined,
        sort_by: sortBy,
        cursor: opts.cursor || undefined,
      };
      const { data } = await api.get('/admin/users', { params });
      setRows(opts.append ? [...rows, ...data.users] : data.users);
      setTotal(data.total_count);
      setNextCursor(data.next_cursor);
      if (!opts.append) setCursor(null);
    } catch (e) {
       
      alert(e?.response?.data?.detail || 'Failed to load users');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    fetchPage();
     
  }, [debouncedQ, role, signupSince, hasReg, sortBy, incompleteNamesFilter]);

  // Iteration 22 hotfix - loud signal on the Users page for silent-fail
  // empty-profile users so admins spot regressions without opening
  // detail pages one by one.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/admin/users/empty-profile-count');
        setEmptyProfileCount(data?.empty_with_rsvp_data ?? 0);
      } catch (_) { /* non-fatal */ }
    })();
    // Bundle 21 hotfix - second loud signal for users whose guest RSVP
    // was never linked to their user_id (Arham state). The backfill op
    // now email-fallback-links these in one Apply pass.
    (async () => {
      try {
        const { data } = await api.get('/admin/users/unlinked-rsvp-count');
        setUnlinkedRsvpCount(data?.users_with_unlinked_rsvps ?? 0);
      } catch (_) { /* noop */ }
    })();
    // Bundle 28 - third loud signal: users with missing / <=2-char names.
    (async () => {
      try {
        const { data } = await api.get('/admin/users/incomplete-names-count');
        setIncompleteNamesCount(data?.count ?? 0);
      } catch (_) { /* noop */ }
    })();
    // Bundle 149b - blocked-under-18 surfacing. Fetch the list so
    // we can render a red badge per row + a filter chip.
    (async () => {
      try {
        const { data } = await api.get('/admin/users/blocked-under-18');
        setBlockedUnder18(data?.users || []);
      } catch (_) { /* noop */ }
    })();
  }, []);

  // Bundle 149b - fast lookup for the row-level badge.
  const blockedIds = useMemo(
    () => new Set(blockedUnder18.map((u) => u.id)),
    [blockedUnder18]);
  const displayRows = useMemo(
    () => (blockedUnder18Filter
        ? rows.filter((r) => blockedIds.has(r.id))
        : rows),
    [rows, blockedUnder18Filter, blockedIds]);

  const refetchAll = async () => {
    await fetchPage();
    try {
      const { data } = await api.get('/admin/users/blocked-under-18');
      setBlockedUnder18(data?.users || []);
    } catch (_) { /* noop */ }
  };

  return (
    <div className="p-8 max-w-7xl" data-testid="admin-user-directory-page">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold text-primary-900 flex items-center gap-2">
            <Users className="h-7 w-7 text-secondary-600" /> User Directory
          </h1>
          <p className="text-sm text-neutral-600 mt-1">
            Browse everyone in the community. Read-only.
            {' '}
            <Link to="/admin/user-roles" className="text-primary-700 hover:underline" data-testid="admin-user-directory-role-link">
              Manage roles &rarr;
            </Link>
          </p>
        </div>
        <div className="text-right text-xs text-neutral-500" data-testid="admin-user-directory-total">
          <span className="text-2xl font-bold text-primary-900">{total.toLocaleString()}</span>
          <div>total users</div>
          {emptyProfileCount != null && emptyProfileCount > 0 && (
            <Link
              to="/admin/maintenance?op=audit-profile-completeness"
              className="mt-2 inline-flex items-center rounded-full border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 text-[11px] font-semibold px-2.5 py-1"
              data-testid="admin-users-empty-profile-badge"
              title="Users with empty profiles despite linked RSVPs. Click to open the audit op."
            >
              {emptyProfileCount} with empty profile
            </Link>
          )}
          {unlinkedRsvpCount != null && unlinkedRsvpCount > 0 && (
            <Link
              to="/admin/maintenance?op=backfill-profiles-from-rsvp"
              className="mt-2 ml-2 inline-flex items-center rounded-full border border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100 text-[11px] font-semibold px-2.5 py-1"
              data-testid="admin-users-unlinked-rsvp-badge"
              title="Users whose guest RSVP was never linked to their user_id. Click to open the backfill op - one Apply fixes both link + hydration."
            >
              {unlinkedRsvpCount} with unlinked RSVPs
            </Link>
          )}
          {incompleteNamesCount != null && incompleteNamesCount > 0 && (
            <button
              type="button"
              onClick={() => setIncompleteNamesFilter((v) => !v)}
              className={`mt-2 ml-2 inline-flex items-center rounded-full border text-[11px] font-semibold px-2.5 py-1 transition-colors ${
                incompleteNamesFilter
                  ? 'border-rose-400 bg-rose-100 text-rose-900'
                  : 'border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100'
              }`}
              data-testid="admin-users-incomplete-names-badge"
              title="Users with a missing or <= 2 character first/last name. Click to filter."
            >
              {incompleteNamesCount} users with incomplete names
              {incompleteNamesFilter ? ' (filtered)' : ''}
            </button>
          )}
          {blockedUnder18.length > 0 && (
            <button
              type="button"
              onClick={() => setBlockedUnder18Filter((v) => !v)}
              className={`mt-2 ml-2 inline-flex items-center rounded-full border text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 transition-colors ${
                blockedUnder18Filter
                  ? 'border-red-600 bg-red-700 text-white'
                  : 'border-red-500 bg-red-600 text-white hover:bg-red-700'
              }`}
              data-testid="admin-users-blocked-under-18-chip"
              title="Users blocked because their DOB indicates they are under 18. Click to filter."
            >
              Under 18 ({blockedUnder18.length})
              {blockedUnder18Filter ? ' - filtered' : ''}
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="md:col-span-2 relative">
          <Search className="h-4 w-4 absolute left-3 top-3 text-neutral-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, phone"
            className="pl-9"
            data-testid="admin-user-directory-filter-search"
          />
        </div>
        <Select value={role || 'all'} onValueChange={(v) => setRole(v === 'all' ? '' : v)}>
          <SelectTrigger data-testid="admin-user-directory-filter-role"><SelectValue placeholder="Any role" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any role</SelectItem>
            <SelectItem value="super_admin">Super admin</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="user">User</SelectItem>
            <SelectItem value="guest">Guest</SelectItem>
          </SelectContent>
        </Select>
        <Select value={signupSince} onValueChange={setSignupSince}>
          <SelectTrigger data-testid="admin-user-directory-filter-signup"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All time</SelectItem>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
          </SelectContent>
        </Select>
        <Select value={hasReg} onValueChange={setHasReg}>
          <SelectTrigger data-testid="admin-user-directory-filter-hasreg"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any registrations</SelectItem>
            <SelectItem value="yes">Has active RSVP</SelectItem>
            <SelectItem value="no">No RSVP yet</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mb-3 flex items-center justify-between text-xs">
        <div className="text-neutral-500">Showing {rows.length} of {total}</div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-56" data-testid="admin-user-directory-sort">
            <ArrowUpDown className="h-3 w-3 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="created_at">Newest first</SelectItem>
            <SelectItem value="created_at_asc">Oldest first</SelectItem>
            <SelectItem value="points_total">Points (high to low)</SelectItem>
            <SelectItem value="registration_count">Registrations (high to low)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
        <div className="grid grid-cols-[auto_2fr_1.2fr_0.6fr_0.9fr_0.7fr_0.7fr_0.6fr_auto] gap-3 px-4 py-2.5 text-[11px] uppercase tracking-widest text-neutral-500 border-b bg-neutral-50">
          <div />
          <div>Name / Email</div>
          <div>Signed up</div>
          <div>Role</div>
          <div>Level / Rank</div>
          <div className="text-right">Points</div>
          <div className="text-right">RSVPs</div>
          <div className="text-right">Referred</div>
          <div />
        </div>
        {displayRows.length === 0 && !loading && (
          <div className="p-10 text-center text-sm text-neutral-500" data-testid="admin-user-directory-empty">
            No users match these filters.
          </div>
        )}
        {displayRows.map((u) => (
          <div
            key={u.id}
            onClick={() => setDrawerUser(u)}
            className="grid grid-cols-[auto_2fr_1.2fr_0.6fr_0.9fr_0.7fr_0.7fr_0.6fr_auto] gap-3 px-4 py-3 border-b last:border-b-0 items-center cursor-pointer hover:bg-primary-50/30 transition-colors"
            data-testid={`admin-user-directory-row-${u.id}`}
          >
            <AvatarCell url={u.primary_photo_thumb_url} name={`${u.first_name || ''} ${u.last_name || ''}`} />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-primary-900 truncate flex items-center gap-2">
                <span className="truncate">{(u.first_name || '') + ' ' + (u.last_name || '')}</span>
                {blockedIds.has(u.id) && (
                  <span
                    className="inline-flex items-center rounded-md bg-red-700 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 whitespace-nowrap"
                    data-testid={`admin-user-blocked-under-18-badge-${u.id}`}
                    role="status"
                    aria-label="This user is blocked because their date of birth indicates they are under 18"
                  >
                    Under 18 - Blocked
                  </span>
                )}
              </div>
              <div className="text-xs text-neutral-500 truncate">{u.email}</div>
              {blockedIds.has(u.id) && (
                <div
                  className="mt-0.5 text-[11px] text-red-800 font-semibold"
                  data-testid={`admin-user-blocked-under-18-msg-${u.id}`}
                >
                  Blocked from login and payment. Click for actions.
                </div>
              )}
              {(() => {
                const fnBad = !u.first_name || String(u.first_name).trim().length <= 2;
                const lnBad = !u.last_name || String(u.last_name).trim().length <= 2;
                if (!fnBad && !lnBad) return null;
                return (
                  <div className="mt-1 flex flex-wrap gap-1" data-testid={`admin-user-row-name-chips-${u.id}`}>
                    {fnBad && (
                      <span
                        className="inline-flex items-center rounded-full border border-rose-200 bg-rose-50 text-rose-800 text-[10px] font-semibold px-1.5 py-0.5"
                        data-testid={`admin-user-row-missing-first-name-${u.id}`}
                      >
                        Missing first name
                      </span>
                    )}
                    {lnBad && (
                      <span
                        className="inline-flex items-center rounded-full border border-rose-200 bg-rose-50 text-rose-800 text-[10px] font-semibold px-1.5 py-0.5"
                        data-testid={`admin-user-row-missing-last-name-${u.id}`}
                      >
                        Missing last name
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="text-xs text-neutral-600">{_fmtDate(u.created_at)}</div>
            <div><RolePill role={u.role} /></div>
            <div><LevelPill prog={u.progression} /></div>
            <div className="text-right text-sm font-semibold text-primary-900">
              {u.progression ? (u.progression.points_total || 0).toLocaleString() : '-'}
            </div>
            <div className="text-right text-sm text-primary-900">{u.registration_count}</div>
            <div className="text-right text-sm text-primary-900">
              {u.referral ? u.referral.referred_count : '-'}
            </div>
            <ChevronRight className="h-4 w-4 text-neutral-400" />
          </div>
        ))}
        {loading && (
          <div className="p-6 text-center text-sm text-neutral-500">Loading...</div>
        )}
      </div>

      {nextCursor && (
        <div className="mt-4 text-center">
          <Button
            variant="outline"
            onClick={() => fetchPage({ cursor: nextCursor, append: true })}
            data-testid="admin-user-directory-load-more"
          >
            Load more
          </Button>
        </div>
      )}

      {/* Detail drawer */}
      <Sheet open={!!drawerUser} onOpenChange={(o) => !o && setDrawerUser(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="admin-user-directory-drawer">
          {drawerUser && (
            <>
              {blockedIds.has(drawerUser.id) && (
                <BlockedUnder18Actions
                  user={drawerUser}
                  onDone={async () => {
                    setDrawerUser(null);
                    await refetchAll();
                  }}
                />
              )}
              <UserDetailPanel userId={drawerUser.id} />
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Detail drawer body: fetches /admin/users/{id}/detail on open and renders
// tabs Profile / Progression / Achievements / Referrals / Registrations.
// ---------------------------------------------------------------------------
const UserDetailPanel = ({ userId }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/users/${userId}/detail`);
        if (mounted) setDetail(data);
      } catch (e) {
         
        console.error('load user detail failed', e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [userId]);

  if (loading) return <div className="p-6 text-sm text-neutral-500">Loading user...</div>;
  if (!detail) return <div className="p-6 text-sm text-neutral-500">Failed to load.</div>;

  const u = detail.user || {};
  const p = u.profile || {};
  const photos = p.photos || [];
  const handles = p.social_handles || {};

  return (
    <div className="space-y-5" data-testid="admin-user-directory-drawer-body">
      <SheetHeader>
        <div className="flex items-start justify-between gap-3 pr-8">
          <SheetTitle className="text-xl">
            {(u.first_name || '') + ' ' + (u.last_name || '')}
          </SheetTitle>
          <Link
            to={`/admin/users/${userId}`}
            className="inline-flex items-center gap-1 text-xs font-semibold text-primary-700 hover:text-primary-900 hover:underline whitespace-nowrap mt-1"
            data-testid="admin-user-detail-open-full-profile"
          >
            Open full profile <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
        <div className="text-xs text-neutral-500 flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {u.email}</span>
          {u.phone && <span className="inline-flex items-center gap-1"><PhoneIcon className="h-3 w-3" /> {u.phone}</span>}
          <RolePill role={u.role} />
        </div>
      </SheetHeader>

      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="grid grid-cols-5 w-full">
          <TabsTrigger value="profile" data-testid="admin-user-detail-tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="progression" data-testid="admin-user-detail-tab-progression">Progression</TabsTrigger>
          <TabsTrigger value="achievements" data-testid="admin-user-detail-tab-achievements">
            Achievements
            {detail.achievement_count_public > 0 && (
              <Badge className="ml-1.5 bg-secondary-500 text-primary-900 h-4 min-w-[16px] px-1 text-[10px]">
                {detail.achievement_count_public}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="referrals" data-testid="admin-user-detail-tab-referrals">Referrals</TabsTrigger>
          <TabsTrigger value="registrations" data-testid="admin-user-detail-tab-registrations">RSVPs</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="pt-4 space-y-4">
          {photos.length > 0 && (
            <div className="grid grid-cols-3 gap-2" data-testid="admin-user-detail-photos">
              {photos.map((ph, i) => (
                <img
                  key={ph.id || i}
                  src={ph.thumb_url || ph.url}
                  alt={`photo-${i}`}
                  className="aspect-square rounded-lg object-cover bg-neutral-100"
                  data-testid={`admin-user-detail-photo-${i}`}
                  loading="lazy"
                />
              ))}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Kv label="Age" value={p.age} />
            <Kv label="Gender" value={p.gender} />
            <Kv label="DOB" value={p.dob} />
            <Kv label="City" value={(p.location || {}).city} />
            <Kv label="State" value={(p.location || {}).state} />
            <Kv label="Country" value={(p.location || {}).country} />
            <Kv label="Occupation" value={p.occupation} />
            <Kv label="Languages" value={Array.isArray(p.languages) ? p.languages.join(', ') : p.languages} />
            <Kv label="Signed up" value={_fmtDate(u.created_at)} />
          </div>
          {(handles.instagram || handles.linkedin || handles.tiktok || handles.twitter || handles.facebook) && (
            <div className="flex items-center gap-3 flex-wrap" data-testid="admin-user-detail-socials">
              {handles.instagram && <SocialLink icon={Instagram} url={`https://instagram.com/${handles.instagram.replace('@','')}`} label={handles.instagram} />}
              {handles.linkedin && <SocialLink icon={Linkedin} url={handles.linkedin.startsWith('http') ? handles.linkedin : `https://linkedin.com/in/${handles.linkedin}`} label="LinkedIn" />}
              {handles.tiktok && <SocialLink icon={Music2} url={`https://tiktok.com/@${handles.tiktok.replace('@','')}`} label={`@${handles.tiktok.replace('@','')}`} />}
              {handles.twitter && <SocialLink icon={Twitter} url={`https://x.com/${handles.twitter.replace('@','')}`} label={handles.twitter} />}
              {handles.facebook && <SocialLink icon={Facebook} url={handles.facebook.startsWith('http') ? handles.facebook : `https://facebook.com/${handles.facebook}`} label="Facebook" />}
            </div>
          )}
          {(p.about_me || p.bio) && (
            <div className="text-sm text-primary-900/90 whitespace-pre-wrap p-3 rounded-lg bg-neutral-50">
              {p.about_me || p.bio}
            </div>
          )}
          {Array.isArray(p.interests) && p.interests.length > 0 && (
            <TagList label="Interests" items={p.interests} testid="admin-user-detail-interests" />
          )}
          {Array.isArray(p.activities) && p.activities.length > 0 && (
            <TagList label="Activities" items={p.activities} testid="admin-user-detail-activities" />
          )}
        </TabsContent>

        <TabsContent value="progression" className="pt-4 space-y-4">
          {detail.progression ? (
            <div className="grid grid-cols-4 gap-3">
              <StatCard label="Level" value={`L${detail.progression.level}`} />
              <StatCard label="Rank" value={detail.progression.rank || detail.progression.stage || '-'} />
              <StatCard label="Total" value={(detail.progression.points_total || 0).toLocaleString()} icon={TrendingUp} />
              <StatCard label="Unlocked" value={detail.achievement_count_public || 0} icon={Trophy} />
            </div>
          ) : (
            <div className="text-sm text-neutral-500">No progression data yet.</div>
          )}
          <div>
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">Recent ledger</div>
            <div className="space-y-1.5" data-testid="admin-user-detail-ledger">
              {(detail.recent_ledger || []).map((row) => (
                <div key={row.id} className="flex items-center justify-between text-xs px-3 py-2 rounded-lg bg-neutral-50">
                  <span className="truncate text-primary-900">{row.reason || row.kind}</span>
                  <span className={`font-semibold ${row.points >= 0 ? 'text-success-700' : 'text-red-600'}`}>
                    {row.points > 0 ? '+' : ''}{row.points}
                  </span>
                </div>
              ))}
              {(!detail.recent_ledger || detail.recent_ledger.length === 0) && (
                <div className="text-xs text-neutral-500">No ledger entries.</div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="achievements" className="pt-4">
          {(detail.achievements || []).length === 0 ? (
            <div className="text-sm text-neutral-500">No achievements unlocked yet.</div>
          ) : (
            <div className="grid grid-cols-4 gap-2" data-testid="admin-user-detail-achievements-grid">
              {detail.achievements.map((a) => (
                <div key={a.code} className="aspect-square rounded-lg border border-secondary-200 bg-gradient-to-br from-secondary-50 to-primary-50 p-2 flex flex-col items-center justify-center text-center">
                  <Trophy className="h-5 w-5 text-secondary-600 mb-1" />
                  <div className="text-[10px] font-semibold text-primary-900 leading-tight break-words">
                    {a.code}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="referrals" className="pt-4 space-y-3">
          {detail.referral_code ? (
            <>
              <div className="grid grid-cols-3 gap-3">
                <StatCard label="Code" value={detail.referral_code.code} icon={Share2} />
                <StatCard label="Landings" value={detail.referral_attributions.length} />
                <StatCard label="Converted" value={(detail.referral_attributions || []).filter(a => a.converted_at).length} />
              </div>
              <div className="text-[11px] uppercase tracking-widest text-neutral-500">Attributions</div>
              <div className="space-y-1.5 max-h-96 overflow-y-auto" data-testid="admin-user-detail-referrals">
                {(detail.referral_attributions || []).map((a) => (
                  <div key={a.id} className="text-xs px-3 py-2 rounded-lg bg-neutral-50 flex items-center justify-between">
                    <span className="text-primary-900 truncate">{a.referred_email || a.referred_user_id}</span>
                    <span className={`font-semibold ${a.converted_at ? 'text-success-700' : 'text-neutral-500'}`}>
                      {a.converted_at ? 'Converted' : 'Landed'}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-sm text-neutral-500">No referral code minted for this user.</div>
          )}
        </TabsContent>

        <TabsContent value="registrations" className="pt-4">
          {(detail.registrations || []).length === 0 ? (
            <div className="text-sm text-neutral-500">No RSVPs yet.</div>
          ) : (
            <div className="space-y-1.5" data-testid="admin-user-detail-registrations">
              {(detail.registrations || []).map((r) => (
                <div key={r.id} className="text-xs px-3 py-2 rounded-lg bg-neutral-50 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-primary-900 truncate">{r.event_id}</div>
                    <div className="text-neutral-500">{_fmtDate(r.created_at)} &middot; {r.status}</div>
                  </div>
                  {r.ticket_code && (
                    <span className="inline-flex items-center gap-1 text-primary-900">
                      <Ticket className="h-3 w-3" /> {r.ticket_code.slice(0, 8)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <div className="pt-2 border-t border-neutral-200 flex items-center justify-between text-xs">
        <span className="text-neutral-500">Role management is separate.</span>
        <Link
          to="/admin/user-roles"
          className="text-primary-700 hover:underline inline-flex items-center gap-1"
          data-testid="admin-user-detail-manage-roles-link"
        >
          Open Roles <ExternalLink className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
};

const Kv = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
    <div className="text-primary-900">{value || '-'}</div>
  </div>
);

const StatCard = ({ label, value, icon: Icon }) => (
  <div className="rounded-lg border border-neutral-200 bg-white p-3">
    <div className="text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-1">
      {Icon && <Icon className="h-3 w-3" />} {label}
    </div>
    <div className="text-lg font-bold text-primary-900 mt-1">{value}</div>
  </div>
);

const TagList = ({ label, items, testid }) => (
  <div data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">{label}</div>
    <div className="flex flex-wrap gap-1.5">
      {items.map((it, i) => (
        <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-primary-50 text-primary-900 border border-primary-100">
          {it}
        </span>
      ))}
    </div>
  </div>
);

const SocialLink = ({ icon: Icon, url, label }) => (
  <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary-700 hover:text-primary-900 inline-flex items-center gap-1 text-xs">
    <Icon className="h-3.5 w-3.5" /> {label}
  </a>
);

// ---------------------------------------------------------------------------
// Bundle 149b - Blocked-under-18 actions block. Rendered at the top
// of the detail drawer for any user whose row carries blocked_under_18.
// Three buttons: Correct DOB, Contact for refund, Delete account.
// ---------------------------------------------------------------------------
const BlockedUnder18Actions = ({ user, onDone }) => {
  const [showDob, setShowDob] = useState(false);
  const [showRefund, setShowRefund] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  return (
    <div
      className="mb-4 rounded-xl border-2 border-red-500 bg-red-50 p-4"
      data-testid="admin-user-blocked-under-18-actions"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div className="text-sm font-bold text-red-900 uppercase tracking-wider">
            Under 18 - Blocked
          </div>
          <p className="text-xs text-red-800 mt-1">
            This account failed the age gate. Login and payment are
            refused. Resolve via one of the three actions below.
          </p>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          size="sm"
          className="bg-primary-700 hover:bg-primary-800 text-white"
          onClick={() => setShowDob(true)}
          data-testid="admin-user-blocked-correct-dob-btn"
        >
          Correct DOB
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="border-red-600 text-red-900 hover:bg-red-100"
          onClick={() => setShowRefund(true)}
          data-testid="admin-user-blocked-refund-btn"
        >
          Contact for refund
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="border-red-600 text-red-900 hover:bg-red-100"
          onClick={() => setShowDelete(true)}
          data-testid="admin-user-blocked-delete-btn"
        >
          Delete account
        </Button>
      </div>
      <CorrectDobModal
        open={showDob}
        onOpenChange={setShowDob}
        user={user}
        onDone={onDone}
      />
      <RefundPendingModal
        open={showRefund}
        onOpenChange={setShowRefund}
        user={user}
        onDone={onDone}
      />
      <DeleteAccountModal
        open={showDelete}
        onOpenChange={setShowDelete}
        user={user}
        onDone={onDone}
      />
    </div>
  );
};


const CorrectDobModal = ({ open, onOpenChange, user, onDone }) => {
  const [dob, setDob] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!dob || reason.trim().length < 6) {
      toast.error('DOB and reason (min 6 chars) are required.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        '/admin/incidents/set-dob-override',
        { user_id: user.id, new_dob: dob, reason: reason.trim() });
      if (data.is_now_adult) {
        toast.success('DOB corrected. Account unblocked.');
      } else {
        toast.success('DOB updated. Account remains blocked (still under 18).');
      }
      onOpenChange(false);
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
        || e?.response?.data?.detail
        || 'DOB override failed.');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="admin-user-correct-dob-modal">
        <DialogHeader>
          <DialogTitle>Correct DOB</DialogTitle>
          <DialogDescription>
            Records an audit row in <code>dob_override_log</code>. If the new
            DOB is adult, the account is unblocked automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="dob-input">New DOB (YYYY-MM-DD)</Label>
            <Input
              id="dob-input" type="date" value={dob}
              onChange={(e) => setDob(e.target.value)}
              data-testid="admin-user-correct-dob-input"
            />
          </div>
          <div>
            <Label htmlFor="reason-input">Reason (audit note)</Label>
            <Textarea
              id="reason-input" rows={3} value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. user provided passport scan confirming adult DOB"
              data-testid="admin-user-correct-dob-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="admin-user-correct-dob-submit">
            {busy ? 'Saving...' : 'Save override'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


const RefundPendingModal = ({ open, onOpenChange, user, onDone }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (note.trim().length < 6) {
      toast.error('Note is required (min 6 chars).');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/mark-refund-pending`,
                          { note: note.trim() });
      toast.success('Marked refund pending.');
      onOpenChange(false);
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to mark refund pending.');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="admin-user-refund-modal">
        <DialogHeader>
          <DialogTitle>Contact for refund</DialogTitle>
          <DialogDescription>
            Stamps <code>refund_pending: true</code> on the user with an
            audit note. The actual refund is processed via the separate
            ops workflow.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label htmlFor="refund-note">Note</Label>
          <Textarea
            id="refund-note" rows={3} value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. paid $99 on 2026-08-24 - refund via original method"
            data-testid="admin-user-refund-note"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="admin-user-refund-submit">
            {busy ? 'Saving...' : 'Mark refund pending'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


const DeleteAccountModal = ({ open, onOpenChange, user, onDone }) => {
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (confirm.toLowerCase() !== 'delete') {
      toast.error('Type DELETE to confirm.');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/gdpr-delete`,
                          { reason: 'blocked_under_18 minor account' });
      toast.success('Account deleted.');
      onOpenChange(false);
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
        || e?.response?.data?.detail
        || 'Delete failed. Check the GDPR endpoint path.');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="admin-user-delete-modal">
        <DialogHeader>
          <DialogTitle>Delete account</DialogTitle>
          <DialogDescription>
            This calls the GDPR delete endpoint. Irreversible. Type
            DELETE to confirm.
          </DialogDescription>
        </DialogHeader>
        <Input
          value={confirm} onChange={(e) => setConfirm(e.target.value)}
          placeholder="Type DELETE"
          data-testid="admin-user-delete-confirm"
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button
            className="bg-red-700 hover:bg-red-800 text-white"
            onClick={submit} disabled={busy}
            data-testid="admin-user-delete-submit"
          >
            {busy ? 'Deleting...' : 'Delete account'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


export default AdminUsersPage;
